/**
 * The closed icon set.
 *
 * ⚠️ INLINE SVG PATHS, NOT AN ICON FONT AND NOT AN UPLOAD. An icon font costs a network round trip
 * before the first icon is visible and renders as a box in the gap; an uploaded SVG is arbitrary
 * markup on a page we publish under our own certificate, and SVG can carry script. A closed set of
 * hand-written paths is a few hundred bytes inlined into the HTML, works offline, and inherits
 * `currentColor` so it is always the template's own accent.
 *
 * ⚠️ `aria-hidden`, ALWAYS. Every icon here sits beside a title that says the same thing. An icon
 * with its own accessible name would make a screen reader announce every feature twice.
 */

import type { ReactNode } from 'react'

const PATHS: Record<string, ReactNode> = {
  star: <path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />,
  check: <path d="M4.5 12.5l5 5 10-11" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.3l3.4 2" />
    </>
  ),
  shield: <path d="M12 3.2l7 2.7v5.4c0 4.3-2.9 8-7 9.5-4.1-1.5-7-5.2-7-9.5V5.9z" />,
  truck: (
    <>
      <path d="M2.8 6.5h10.4v9.2H2.8z" />
      <path d="M13.2 9.6h3.9l3.1 3.1v3h-7z" />
      <circle cx="7" cy="17.6" r="1.9" />
      <circle cx="17" cy="17.6" r="1.9" />
    </>
  ),
  heart: <path d="M12 20.2S3.8 15.3 3.8 9.6a4.4 4.4 0 018.2-2.3 4.4 4.4 0 018.2 2.3c0 5.7-8.2 10.6-8.2 10.6z" />,
  spark: <path d="M12 2.8l1.9 6.1 6.1 1.9-6.1 1.9-1.9 6.1-1.9-6.1-6.1-1.9 6.1-1.9z" />,
}

export const ICON_NAMES = Object.keys(PATHS)

export function Icon({ name, className }: { name: string; className?: string }) {
  const path = PATHS[name]
  if (!path) return null
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  )
}
